import React from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import { GBToBytes } from "@sscs/utils";
import { AnalysisCapacityFields } from "Components";
import { GetOrganizationUsageResponse, GroupUsage } from "Types";
import { apiErrorToast } from "Utils";
import { useLimit } from "../../hooks";
import { GroupLimitForm, GroupLimitFormPercentageSlider } from "../../types";

const filestreamPercentageFieldName: keyof GroupLimitFormPercentageSlider =
  "filestream_percentage";
const projectsPercentageFieldName: keyof GroupLimitFormPercentageSlider =
  "projects_percentage";

type AddLimitProps = {
  organizationUsage: GetOrganizationUsageResponse;
  groupUsage: GroupUsage | null;
  isOpen: boolean;
  onClose: () => void;
};

const AddLimitContent = ({
  organizationUsage,
  groupUsage,
  onClose,
}: Omit<AddLimitProps, "isOpen">): React.JSX.Element => {
  const {
    methods,
    createGroupUsageLimit,
    totalPortalCapacity,
    groups,
    filestreamPercentage,
    projectsPercentage,
    onPercentageSliderChange,
    onProjectsPercentageInputChange,
  } = useLimit(
    filestreamPercentageFieldName,
    projectsPercentageFieldName,
    organizationUsage,
    groupUsage,
  );

  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<GroupLimitForm> = async (data) => {
    try {
      await createGroupUsageLimit({
        body: {
          group_ids: data.groups,
          limit: {
            projects_percentage:
              data.filestream_percentage === null
                ? null
                : 100 - data.filestream_percentage,
            limit_bytes: GBToBytes(data.limited),
          },
        },
      }).unwrap();

      Toast.show({
        title: groupUsage?.limit
          ? "Edited group usage limit"
          : `Added usage limit for ${data.groups.length} group${
              data.groups.length !== 1 ? "s" : ""
            }`,
        type: "success",
      });
    } catch (error) {
      apiErrorToast(
        error,
        `Could not ${groupUsage?.limit ? "edit" : "add"} usage limit`,
      );
    }

    onClose();
  };

  return (
    <FormProvider {...methods}>
      <Form.Form onSubmit={handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>
            {groupUsage
              ? `${groupUsage.limit ? "Edit" : "Add"} monthly usage limit for ${
                  groupUsage.group_name
                }`
              : "Add monthly usage limit for one or more groups"}
          </Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Content>
              {!groupUsage && (
                <Form.Autocomplete
                  name="groups"
                  label="Select groups"
                  isRequired
                  errors={errors}
                  options={organizationUsage.groups_without_limits.map(
                    (group) => group.group_id,
                  )}
                  multiSelect={true}
                  getOptionLabel={(option) =>
                    organizationUsage.groups_without_limits.find(
                      (group) => group.group_id === option,
                    )?.group_name ?? ""
                  }
                />
              )}
              <AnalysisCapacityFields
                groupsCount={groups.length}
                totalPortalCapacity={totalPortalCapacity}
                filestreamUsageBytes={groupUsage?.filestream_usage_bytes ?? 0}
                projectsUsageBytes={groupUsage?.projects_usage_bytes ?? 0}
                filestreamPercentageFieldName={filestreamPercentageFieldName}
                projectsPercentageFieldName={projectsPercentageFieldName}
                filestreamPercentage={filestreamPercentage}
                projectsPercentage={projectsPercentage}
                isRequired={true}
                onPercentageSliderChange={onPercentageSliderChange}
                onProjectsPercentageInputChange={
                  onProjectsPercentageInputChange
                }
              />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              aria-label={`${groupUsage?.limit ? "edit" : "add"} limit`}
            >
              {groupUsage?.limit ? "Edit" : "Add"} Limit
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const AddLimit = ({ isOpen, ...props }: AddLimitProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <AddLimitContent {...props} />
  </Dialog.Frame>
);

export default AddLimit;
